/** Shared public Assist contracts. A carry is human writing, never approval,
 * publication authority, an original operation, or a private-to-public conversion. */
import { z } from 'zod';
import type { ApproveCoachingFeedbackRequest, CoachingFeedbackAcceptedResponse, CoachingGoalAcceptedResponse } from '@/lib/types';
import { classifyPrivateApprovalFailure, decodePrivateFeedbackReviewCarry } from './privateFeedbackReview';

export type CanonicalFeedbackReviewCarry = {
  content: ApproveCoachingFeedbackRequest['content'];
  recording_entry_id?: string;
  recording?: {
    video_reference: NonNullable<ApproveCoachingFeedbackRequest['video_reference']>;
    recording_resource: NonNullable<ApproveCoachingFeedbackRequest['recording_resource']>;
  };
};
/** Selected only by the explicit recording-navigation action. Retained public
 * and private writing may coexist; presence of either never chooses an intent. */
export type CanonicalReviewCarryFields = {
  canonicalFeedback?: CanonicalFeedbackReviewCarry;
  recordingIntent?: 'private' | 'public';
};

/** The public and private full-content shapes share validation, not storage or
 * selection. Callers keep them under separate exact-origin carry fields and
 * never invoke this to copy one into the other. Raw strings stay unchanged. */
export function decodeCanonicalFeedbackReviewCarry(value: unknown): CanonicalFeedbackReviewCarry | null {
  return decodePrivateFeedbackReviewCarry(value);
}

const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
const decimalId = z.string().regex(/^[1-9][0-9]{0,18}$/)
  .refine(value => /^[1-9][0-9]{0,18}$/.test(value) && BigInt(value) <= 9223372036854775807n);
const feedbackReceipt = z.object({
  schema_version: z.literal('coaching-feedback-accepted.v1'),
  artifact_kind: z.literal('feedback'), state: z.literal('published_at_acceptance'),
  feedback_id: uuid, revision_id: uuid, version: z.number().int().min(1).max(2147483647),
}).strict();
const goalReceipt = z.object({
  schema_version: z.literal('coaching-goal-accepted.v1'),
  artifact_kind: z.literal('goal'), state: z.literal('created_at_acceptance'),
  goal_id: decimalId, fingerprint_schema: z.literal('goal-accepted-result.v1'),
  fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
}).strict();
/** Tagged historical acceptance only. Fresh parent authority/CAS must be read
 * by the host before any explicit Open/Edit action. */
export function decodeCoachingFeedbackReceipt(value: unknown): CoachingFeedbackAcceptedResponse | null {
  const parsed = feedbackReceipt.safeParse(value);
  return parsed.success ? parsed.data : null;
}
export function decodeCoachingGoalReceipt(value: unknown): CoachingGoalAcceptedResponse | null {
  const parsed = goalReceipt.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Same transactional endpoint and sticky original-operation uncertainty rule.
 * A later error cannot disprove an earlier unknown acceptance in any family. */
export const classifyCanonicalApprovalFailure = classifyPrivateApprovalFailure;
